import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useIntroNote } from '@/src/renderer/lib/intro'
import type { LifeSpace, Task } from '@/src/renderer/lib/db/models'
import type { TaskCreationData } from '../components/TaskEntry'
import { toCreateTask } from '../utils/toCreateTask'
import { orderAtEndOf, orderAtTopOfQuickNotes } from '../utils/appendTaskOrders'
import { quickAddCreatedDetails } from '../utils/quickAddCreatedToast'

/** So lange steht der Hinweis nach ⌘↵. */
const NOTICE_MS = 1600

export interface QuickAddSessionOptions {
  /** Der Bereich, mit dem eine leere Karte beginnt — der, den das Hauptfenster gerade zeigt. */
  startLifeSpace: () => LifeSpace
  /** Das im Hauptfenster offene Projekt — bei gleichem Namen hinter `#` gewinnt es. */
  startProjectId?: () => string | null
  /** Die Hülle verschwindet (Fenster verstecken, Dialog zu). */
  onClose: () => void
  /**
   * Eine Aufgabe ist angelegt — mit ihr, ihrem Bereich und der Zeile mit Ort und Angaben. Das Fenster
   * stößt damit den Sync im Hauptfenster an, und dort sagt ein Toast, wo sie liegt (Sven, 04.10.2026).
   */
  onCreated?: (created: QuickAddCreated) => void
}

/** Wie gespeichert wird: Enter schließt, ⌘↵ bleibt offen, ⇧↵ schließt und springt zur Aufgabe. */
export type QuickAddSaveMode = 'close' | 'keepOpen' | 'reveal'

/** Was nach dem Anlegen weitergeht: die Aufgabe, ihr Bereich und die zweite Zeile des Toasts. */
export interface QuickAddCreated {
  task: Task
  lifeSpace: LifeSpace
  /** Ort, Bereich und Angaben: „Demnächst · Arbeit · morgen · 10m · #Garten“ (`quickAddCreatedDetails`). */
  details: string
  /** ⇧↵: Die Hülle springt zur Aufgabe statt den Toast zu zeigen (`.claude/tasks/quick-add-reveal.md`). */
  reveal: boolean
}

/** Nichts getippt, nichts gesetzt — dann darf die Karte beim Zeigen den aktiven Bereich neu lesen. */
function isEmptyDraft(data: TaskCreationData): boolean {
  return !data.title.trim() && !data.description?.trim() && !data.startDate && !data.dueDate
    && !data.tags?.length && !data.attention && !data.estimatedDuration && !data.recurrence
    && !data.subtasks?.length && !data.rewardId
}

/**
 * **Was beide Hüllen der Schnelleingabe gleich tun** (#234) — das Fenster auf dem Desktop und der
 * Dialog im Web: anlegen, verwerfen, der Hinweis nach ⌘↵, und welcher Bereich gilt.
 *
 * Solange die Karte **leer** ist (nichts getippt, kein Chip, kein Bereich von Hand gewählt), liest
 * `begin()` beim Zeigen den aktiven Bereich neu. Wer mit Entwurf daneben geklickt hat, findet Entwurf
 * UND gewählten Bereich wieder (F3).
 */
export function useQuickAddSession({ startLifeSpace, startProjectId, onClose, onCreated }: QuickAddSessionOptions) {
  const { t, i18n } = useTranslation()
  const source = useDataSource()
  const noteIntro = useIntroNote()
  // Für die Tags in der zweiten Zeile des Toasts.
  const allTags = source.useTags()
  const [lifeSpace, setShownLifeSpace] = useState<LifeSpace>(startLifeSpace)
  const [preferredProjectId, setPreferredProjectId] = useState<string | null>(() => startProjectId?.() ?? null)
  const [resetKey, setResetKey] = useState(0)
  const [notice, setNotice] = useState<string | null>(null)
  /** Getipptes oder gesetzte Chips. */
  const draftRef = useRef(false)
  /** Den Bereich von Hand gewählt. */
  const pickedRef = useRef(false)
  /** Ein Speichern läuft — ein zweites Enter darf nicht noch eine Aufgabe anlegen. */
  const savingRef = useRef(false)
  const startRef = useRef(startLifeSpace)
  startRef.current = startLifeSpace
  const startProjectRef = useRef(startProjectId)
  startProjectRef.current = startProjectId

  /** Die Hülle erscheint. */
  const begin = useCallback(() => {
    if (draftRef.current || pickedRef.current) return
    setShownLifeSpace(startRef.current())
    setPreferredProjectId(startProjectRef.current?.() ?? null)
  }, [])

  /** Bereich im Ziel von Hand gewählt — gilt bis Speichern oder Verwerfen. */
  const setLifeSpace = useCallback((space: LifeSpace) => {
    pickedRef.current = true
    setShownLifeSpace(space)
  }, [])

  /** Jede Änderung der Eingabe (`onDraftChange` der Karte). */
  const noteDraft = useCallback((data: TaskCreationData) => {
    draftRef.current = !isEmptyDraft(data)
  }, [])

  const forget = () => {
    draftRef.current = false
    pickedRef.current = false
  }

  const close = useCallback(() => {
    setNotice(null)
    onClose()
  }, [onClose])

  /** Esc, „Abbrechen“. */
  const discard = useCallback(() => {
    setResetKey(key => key + 1)
    forget()
    close()
  }, [close])

  /**
   * Enter, „Anlegen“ — oder mit `keepOpen` ⌘↵, mit `reveal` ⇧↵. Mit `#Projekt` im Titel dorthin, ans Ende, in dessen
   * Bereich (#234); sonst ohne Projekt, also in Kurz notiert — dort ganz oben, die neueste zuerst (#247) —,
   * mit Wann in Demnächst.
   */
  const save = useCallback(async (data: TaskCreationData, mode: QuickAddSaveMode = 'close') => {
    if (savingRef.current) return
    savingRef.current = true
    const { target } = data
    const space = target?.lifeSpace ?? lifeSpace
    let task: Task
    try {
      const order = target
        ? await orderAtEndOf(source, target.projectId, target.sectionId)
        : data.startDate ? undefined : await orderAtTopOfQuickNotes(source, space)
      task = await source.createTask(toCreateTask(data, target
        ? { lifeSpace: space, projectId: target.projectId, sectionId: target.sectionId, order }
        : { lifeSpace: space, projectId: null, sectionId: null, order }))
    } finally {
      savingRef.current = false
    }
    // Einführung (#323): „Entdecken › Schnelleingabe“; landet sie in Kurz notiert, ist es auch eine eigene Notiz.
    noteIntro('quickadd')
    if (!target && !data.startDate) noteIntro('quicknotes')
    const place = target ? target.projectName || t('project.defaultName') : data.startDate ? t('upcoming.title') : t('quickNotes.title')
    const spaceName = t(`lifeSpaces.${space}`)
    const tagNames = (task.tags ?? []).flatMap(id => allTags?.find(tag => tag.id === id)?.name ?? [])
    const details = quickAddCreatedDetails(task, { place: `${place} · ${spaceName}`, tagNames, locale: i18n.language ?? 'de', t })
    onCreated?.({ task, lifeSpace: space, details, reveal: mode === 'reveal' })
    setResetKey(key => key + 1)
    if (mode === 'keepOpen') {
      setNotice(t('quickAdd.notice', { place, space: spaceName }))
      return
    }
    forget()
    close()
  }, [source, lifeSpace, onCreated, t, i18n.language, allTags, close, noteIntro])

  useEffect(() => {
    if (!notice) return
    const timer = setTimeout(() => setNotice(null), NOTICE_MS)
    return () => clearTimeout(timer)
  }, [notice])

  return { lifeSpace, setLifeSpace, preferredProjectId, resetKey, notice, begin, noteDraft, save, discard }
}
